import { aliasKey, containsTerm } from './debug-data-terms';

export function buildScopeWhere({ composedMap, entity, scopeValues }) {
  const terms = [];
  const contributingActions = [];

  for (const [action, entry] of Object.entries(composedMap)) {
    const entryTerms = collectEntryTerms(entry, entity, scopeValues);
    if (entryTerms.length === 0) continue;

    contributingActions.push(action);
    terms.push(...entryTerms);
  }

  return {
    _or: dedupeTerms(terms),
    action: { _in: contributingActions },
    _and: buildContradictionTerms({ composedMap, entity, scopedId: scopeValues.id }),
  };
}

// A child's rows in its parent's view are only the link rows naming both, never the child's own
// history and never its links to other parents.
export function buildLinkScopeWhere({ composedMap, parentEntity, parentId, childEntity, childIds }) {
  const terms = [];
  const linkActions = [];

  for (const [action, entry] of Object.entries(composedMap)) {
    const parentKey = findParentRefKey(entry, parentEntity);
    const childKey = findParentRefKey(entry, childEntity);
    if (!parentKey || !childKey) continue;

    linkActions.push(action);
    for (const childId of childIds) {
      terms.push({ debugData: { _contains: { [parentKey]: parentId, [childKey]: childId } } });
    }
  }

  return { _or: dedupeTerms(terms), action: { _in: linkActions } };
}

function findParentRefKey(entry, entity) {
  return (entry.parentRefs ?? []).find(ref => ref.entity === entity)?.key;
}

function collectEntryTerms(entry, entity, scopeValues) {
  const terms = [];

  // idKeys identify the entry's OWN entity; a reference from elsewhere is parentRefs/
  // parentNameKeys/parentValueKeys below, never idKeys.
  if (entry.entity === entity) {
    for (const alias of entry.idKeys ?? []) {
      pushContainsTerm(terms, aliasKey(alias), scopeValues.id);
    }
  }

  // compositeIdKeys has no per-member entity of its own, so a tuple member's entity comes from the
  // parentRefs every compositeIdKeys entry declares in parallel (the entry schema enforces it).
  for (const ref of entry.parentRefs ?? []) {
    if (ref.entity === entity) pushContainsTerm(terms, ref.key, scopeValues.id);
  }

  for (const ref of entry.parentNameKeys ?? []) {
    if (ref.entity === entity) pushContainsTerm(terms, ref.key, scopeValues.name);
  }

  for (const ref of entry.parentValueKeys ?? []) {
    if (ref.entity === entity) pushContainsTerm(terms, ref.key, scopeValues[ref.entityField]);
  }

  return terms;
}

function pushContainsTerm(terms, key, value) {
  if (value == null) return; // caller's scopeValues doesn't carry this field — emit nothing, never `_contains: {key: undefined}`
  terms.push(containsTerm(key, value));
}

function dedupeTerms(terms) {
  const seen = new Set();
  const unique = [];
  for (const term of terms) {
    const fingerprint = JSON.stringify(term);
    if (seen.has(fingerprint)) continue;
    seen.add(fingerprint);
    unique.push(term);
  }
  return unique;
}

// Every role receives the same rows for the same scope, so the contradiction filter cannot live on
// the client: filtering fetched rows under-fills the page and desyncs the total count from what is
// rendered.
function buildContradictionTerms({ composedMap, entity, scopedId }) {
  if (scopedId == null) return [];

  return [...collectIdKeyNames(composedMap, entity)].map(name => ({
    _or: [{ _not: { debugData: { _has_key: name } } }, containsTerm(name, scopedId), containsTerm(name, null)],
  }));
}

function collectIdKeyNames(composedMap, entity) {
  const names = new Set();

  for (const entry of Object.values(composedMap)) {
    if (entry.entity === entity) {
      for (const alias of entry.idKeys ?? []) names.add(aliasKey(alias));
    }
    for (const ref of entry.parentRefs ?? []) {
      if (ref.entity === entity) names.add(ref.key);
    }
  }

  return names;
}
